import { BabylonLineLight } from '../../../BabylonLineLight.js';
import { Vector3 as BABYLON_Vector3, Quaternion as BABYLON_Quaternion } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Light } from './Light.js';
import { IVector3, LightType } from '../../../../api.js';
import { radians, degrees } from '../../../../Math.js';
import { LineLightVisualizer } from '../debug_visualizers/LineLightVisualizer.js';
import { vector_3 } from '../../../utility/Conversion.js';
import { calculate_world_transform, calculate_transform_from_parent_world_transform } from '../../../utility/TransformHelper.js';
import { ViewerMaterial } from '../../../material/ViewerMaterial.js';
import { SpotLight } from './SpotLight.js';
var LineLightIntensityMode;
(function (LineLightIntensityMode) {
    LineLightIntensityMode[LineLightIntensityMode["LUMEN"] = 0] = "LUMEN";
    LineLightIntensityMode[LineLightIntensityMode["LUMEN_PER_METER"] = 1] = "LUMEN_PER_METER";
    LineLightIntensityMode[LineLightIntensityMode["CANDELA"] = 2] = "CANDELA";
    LineLightIntensityMode[LineLightIntensityMode["DIRECT"] = 3] = "DIRECT";
})(LineLightIntensityMode || (LineLightIntensityMode = {}));
// In the shader and in our calculations in here, we support separating inner and outer cone angles
// But we don't support setting it, and we don't have any way to send it to the shader as well today.
const INNER_ANGLE_RADIANS = 0.0;
export class LineLight extends Light {
    constructor(viewer, position = { x: 0, y: 0, z: 0 }) {
        super(viewer);
        this.shadow = null;
        // The reason for these odd initialization values, is to properly dirty necessary things in the constructor when setting actual defaults
        this._angle_range = -1;
        this._angle_direction = -1;
        // The rotation around the y axis for the linelight
        this._rotation = -1;
        this._width = -1;
        this._hard_side_cutoff = false;
        this._clip_plane = null;
        this._line_a = new BABYLON_Vector3();
        this._line_b = new BABYLON_Vector3();
        this._line_normal = new BABYLON_Vector3();
        this.type = LightType.LINE_LIGHT;
        this._babylon_light = new BabylonLineLight(`Light_${this.id} (LineLight)`, vector_3(position), vector_3(this._line_a), vector_3(this._line_b), this.angle_range_radians, viewer.scene, this.clip_plane);
        this._intensity_mode = LineLightIntensityMode.DIRECT;
        this._physical_intensity = this.intensity;
        // Set actual defaults, also dirties and recalculates certain things
        this.angle_range_degrees = 60.0;
        this.angle_direction_degrees = 0.0;
        this.rotation_degrees = 0.0;
        this.width = 1.0;
        this.calculate_line_normal();
        this._babylon_light.falloffType = BabylonLineLight.FALLOFF_GLTF;
        ViewerMaterial.active_line_lights.push(this._babylon_light);
        this.position = position;
    }
    get intensity() {
        return super.intensity;
    }
    set intensity(intensity) {
        this._intensity_mode = LineLightIntensityMode.DIRECT;
        this._physical_intensity = intensity;
        super.intensity = intensity;
    }
    set_intensity_lumen(lumen) {
        this._intensity_mode = LineLightIntensityMode.LUMEN;
        this._physical_intensity = lumen;
        this.recalculate_intensity();
    }
    set_intensity_candela(candela) {
        // Since we use candela as the internal unit,
        // we don't need to consider the cone angle when
        // setting the intensity in candela.
        this._intensity_mode = LineLightIntensityMode.CANDELA;
        this._physical_intensity = candela;
        this.recalculate_intensity();
    }
    set_intensity_lumen_per_meter(lumen_per_meter) {
        this._intensity_mode = LineLightIntensityMode.LUMEN_PER_METER;
        this._physical_intensity = lumen_per_meter;
        this.recalculate_intensity();
    }
    recalculate_intensity() {
        if (this._intensity_mode === LineLightIntensityMode.DIRECT)
            return;
        if (this._intensity_mode === LineLightIntensityMode.CANDELA) {
            super.intensity = this._physical_intensity;
        }
        else if (this._intensity_mode === LineLightIntensityMode.LUMEN) {
            super.intensity =
                this._physical_intensity /
                    LineLight.calculate_light_weighted_surface_area(this.width, INNER_ANGLE_RADIANS, this.angle_range_radians);
        }
        else if (this._intensity_mode === LineLightIntensityMode.LUMEN_PER_METER) {
            super.intensity =
                this._physical_intensity /
                    LineLight.calculate_light_weighted_surface_area(1.0, INNER_ANGLE_RADIANS, this.angle_range_radians);
        }
        else {
            throw new Error('Unknown intensity mode for line light: ' + this._intensity_mode);
        }
    }
    set angle_range_radians(angle_range) {
        if (angle_range === this._angle_range)
            return;
        this._angle_range = angle_range;
        this.recalculate_intensity();
        this.mark_dirty(LineLight.DIRTY_ANGLE_RANGE);
    }
    get angle_range_radians() {
        return this._angle_range;
    }
    set angle_range_degrees(angle_range) {
        this.angle_range_radians = radians(angle_range);
    }
    get angle_range_degrees() {
        return degrees(this.angle_range_radians);
    }
    set angle_direction_radians(angle_direction) {
        if (angle_direction === this._angle_direction)
            return;
        this._angle_direction = angle_direction;
        this.calculate_line_normal();
    }
    get angle_direction_radians() {
        return this._angle_direction;
    }
    set angle_direction_degrees(angle_direction) {
        this.angle_direction_radians = radians(angle_direction);
    }
    get angle_direction_degrees() {
        return degrees(this.angle_direction_radians);
    }
    set rotation_radians(rotation) {
        if (rotation === this._rotation)
            return;
        this._rotation = rotation;
        this.recalculate_a_and_b_and_line_normal(LineLight.DIRTY_LINE_NORMAL);
    }
    get rotation_radians() {
        return this._rotation;
    }
    set rotation_degrees(rotation) {
        this.rotation_radians = radians(rotation);
    }
    get rotation_degrees() {
        return degrees(this.rotation_radians);
    }
    set width(width) {
        if (this._width === width)
            return;
        this._width = width;
        this.recalculate_intensity();
        this.recalculate_a_and_b_and_line_normal(LineLight.DIRTY_LINE_WIDTH);
    }
    get width() {
        return this._width;
    }
    get clip_plane() {
        return this._clip_plane;
    }
    set clip_plane(plane) {
        if (this._clip_plane !== null && plane !== null) {
            if (this._clip_plane.normal.x === plane.normal.x &&
                this._clip_plane.normal.y === plane.normal.y &&
                this._clip_plane.normal.z === plane.normal.z &&
                this._clip_plane.d === plane.d)
                return;
        }
        else if (this._clip_plane === null && plane === null)
            return;
        this._clip_plane = plane;
        this.mark_dirty(LineLight.DIRTY_CLIP_PLANE);
    }
    get hard_side_cutoff() {
        return this._hard_side_cutoff;
    }
    /*
    * Enables or disables hard side cutoff for this line light, which is whether the light contribution should stop
    * abruptly for geometry outside of the width of the linelight, or if it should fade out smoothly from the ends of the line
    */
    set hard_side_cutoff(hard_side_cutoff) {
        if (this._hard_side_cutoff === hard_side_cutoff)
            return;
        this._hard_side_cutoff = hard_side_cutoff;
        this.mark_dirty(LineLight.DIRTY_HARD_SIDE_CUTOFF);
    }
    get_world_endpoints() {
        const offset = this.position;
        const offseted_line_a = new BABYLON_Vector3(offset.x + this._line_a.x, offset.y + this._line_a.y, offset.z + this._line_a.z);
        const offseted_line_b = new BABYLON_Vector3(offset.x + this._line_b.x, offset.y + this._line_b.y, offset.z + this._line_b.z);
        if (!this._parent) {
            return {
                line_a: offseted_line_a,
                line_b: offseted_line_b
            };
        }
        const parent_world_transform = calculate_world_transform(this._parent);
        const line_a_transform = calculate_transform_from_parent_world_transform(parent_world_transform, offseted_line_a);
        const line_b_transform = calculate_transform_from_parent_world_transform(parent_world_transform, offseted_line_b);
        return {
            line_a: vector_3(line_a_transform.position),
            line_b: vector_3(line_b_transform.position)
        };
    }
    get_world_line_normal() {
        if (!this._parent) {
            return this.get_line_normal();
        }
        const parent_world_transform = calculate_world_transform(this._parent);
        const rotation = parent_world_transform.rotation;
        const rotation_quaternion = BABYLON_Quaternion.RotationYawPitchRoll(rotation.y, rotation.x, rotation.z);
        const current_line_normal = this.get_line_normal();
        const rotated_line_normal = new BABYLON_Vector3(current_line_normal.x, current_line_normal.y, current_line_normal.z);
        rotated_line_normal.rotateByQuaternionToRef(rotation_quaternion, rotated_line_normal);
        return rotated_line_normal;
    }
    update() {
        this.update_color_and_intensity();
        this.update_parent_material();
        if (this._parent) {
            const parent_updated = this.is_dirty(Light.DIRTY_PARENT_ADDED |
                Light.DIRTY_PARENT_TRANSFORM |
                Light.DIRTY_POSITION |
                LineLight.DIRTY_LINE_NORMAL |
                LineLight.DIRTY_LINE_WIDTH);
            if (parent_updated) {
                const { line_a, line_b } = this.get_world_endpoints();
                this._babylon_light.line_a = vector_3(line_a);
                this._babylon_light.line_b = vector_3(line_b);
                this._babylon_light.line_normal = vector_3(this.get_world_line_normal());
                this.mark_dirty(Light.DIRTY_SHADOW_MAP);
            }
        }
        else {
            if (this.is_dirty(Light.DIRTY_POSITION | LineLight.DIRTY_LINE_NORMAL | LineLight.DIRTY_LINE_WIDTH)) {
                this._babylon_light.position.set(this.position.x, this.position.y, this.position.z);
                const a = this._line_a;
                const b = this._line_b;
                this._babylon_light.line_a.set(a.x + this.position.x, a.y + this.position.y, a.z + this.position.z);
                this._babylon_light.line_b.set(b.x + this.position.x, b.y + this.position.y, b.z + this.position.z);
                this._babylon_light.line_normal = vector_3(this.get_line_normal());
                this.mark_dirty(Light.DIRTY_SHADOW_MAP);
            }
        }
        if (this.is_dirty(LineLight.DIRTY_ANGLE_RANGE)) {
            this._babylon_light.angle_range_radians = this.angle_range_radians;
        }
        if (this.is_dirty(LineLight.DIRTY_HARD_SIDE_CUTOFF)) {
            this._babylon_light.hard_side_cutoff = this.hard_side_cutoff;
        }
        if (this.is_dirty(LineLight.DIRTY_CLIP_PLANE)) {
            this._babylon_light.clip_plane = this.clip_plane;
        }
    }
    dispose() {
        const pbr_mat_active_line_lights = ViewerMaterial.active_line_lights;
        const index = pbr_mat_active_line_lights.indexOf(this._babylon_light);
        if (index !== -1) {
            ViewerMaterial.active_line_lights.splice(index, 1);
        }
        else {
            console.error('Tried to remove a Linelight that was not active');
        }
        super.dispose();
    }
    create_debug_geometry() {
        const { line_manager, light_system, scene } = this._viewer;
        this._debug_geometry = new LineLightVisualizer(this, line_manager, light_system.debug_color_mode, scene);
    }
    get_line_normal() {
        return new BABYLON_Vector3(this._line_normal.x, this._line_normal.y, this._line_normal.z);
    }
    calculate_line_normal() {
        const line_a_to_b = IVector3.subtract(this._line_b, this._line_a);
        const ideal_normal = new IVector3(0.0, 1.0, 0.0);
        // Make the ideal normal perpendicular to the line
        const ideal_crossed_with_line = IVector3.cross(line_a_to_b, ideal_normal);
        const ideal_crossed_with_line_crossed_again = IVector3.cross(ideal_crossed_with_line, line_a_to_b);
        this._line_normal = IVector3.normalize(ideal_crossed_with_line_crossed_again);
        this._line_normal.y = -this._line_normal.y;
        const rotation_quarternion = new BABYLON_Quaternion();
        BABYLON_Quaternion.RotationAxisToRef(new BABYLON_Vector3(line_a_to_b.x, line_a_to_b.y, line_a_to_b.z), this._angle_direction, rotation_quarternion);
        this._line_normal = new BABYLON_Vector3(this._line_normal.x, this._line_normal.y, this._line_normal.z).rotateByQuaternionToRef(rotation_quarternion, new BABYLON_Vector3(0.0, 0.0, 0.0));
        this.mark_dirty(LineLight.DIRTY_ANGLE_RANGE);
    }
    recalculate_a_and_b_and_line_normal(flags) {
        this._line_a.x = 0.0;
        this._line_a.y = 0.0;
        this._line_a.z = -this._width / 2.0;
        this._line_b.x = 0.0;
        this._line_b.y = 0.0;
        this._line_b.z = this._width / 2.0;
        const rotation_quarternion = new BABYLON_Quaternion();
        BABYLON_Quaternion.RotationYawPitchRollToRef(this._rotation, 0.0, 0.0, rotation_quarternion);
        this._line_a.rotateByQuaternionToRef(rotation_quarternion, this._line_a);
        this._line_b.rotateByQuaternionToRef(rotation_quarternion, this._line_b);
        this.calculate_line_normal();
        this.mark_dirty(flags);
    }
    static calculate_light_weighted_surface_area(length, inner_angle_radians, outer_angle_radians) {
        // To calculate the weighted surface area of this "light", we divide it into a cylinder that has 2 spherical caps on the ends, which is how the linelight works.
        // The caps (2 halves) is the same as one spotlight
        const caps_weighted_surface_area = SpotLight.calculate_light_weighted_surface_area(inner_angle_radians, outer_angle_radians);
        // For the cylindrical part, calculating the surface area would be as simple as length * angle_range_radians,
        // but since we have a penumbra on the light, we must weigh our surface area.
        // First we declare our half angles. We only want to calculate this for the half cylinder, otherwise our cosines and sines will be weird.
        // we compensate by multiplying the area by 2 at the end.
        const a = inner_angle_radians * 0.5;
        const b = outer_angle_radians * 0.5;
        // We approximate how Babylon calculates the penumbra (angle falloff) by saying the angle falloff.
        //    angle_falloff(x) = saturate((x - cos(b)) / (cos(a) - cos(b)))
        //
        // thus we can solve the weighted surface area with the integral:
        //    from 0 to b, integrate for x: angle_falloff(cos(x)) dx
        // integrating the saturation function is not trivial, so we can split the integral for the part where the falloff is always 1 (between 0 and a),
        // and for where the falloff goes from 1 to 0 (a to b).
        // The integral between 0 and a is just the surface area of the cylinder section with a cone angle of a, which means the "inner" cylinder part is just:
        const inner_constant_cylinder_surface_area = 2.0 * length * a;
        // (remember we multiply by 2 because we're working with our half-angles)
        // For the outer/penumbra part, our integral for the weighted surface area becomes:
        //    from a to b, integrate for x: (cos x - cos b) / (cos a - cos b)
        // the solution for this integral, multiplied by 2 and the length of the cylinder, then becomes:
        const outer_weighted_cylinder_surface_area = 2.0 * length * ((Math.sin(b) - Math.sin(a)) - Math.cos(b) * (b - a)) / (Math.cos(a) - Math.cos(b));
        // combine the areas:
        return caps_weighted_surface_area + inner_constant_cylinder_surface_area + outer_weighted_cylinder_surface_area;
    }
}
LineLight.DIRTY_LINE_WIDTH = 134217728;
LineLight.DIRTY_ANGLE_RANGE = 268435456;
LineLight.DIRTY_LINE_NORMAL = 536870912;
LineLight.DIRTY_HARD_SIDE_CUTOFF = 1073741824;
LineLight.DIRTY_CLIP_PLANE = 2147483648;
//# sourceMappingURL=LineLight.js.map